import DynamicForm from '@remote-com/mushu/src/components/Form/DynamicForm';
import { SuccessTitle, SuccessDescription } from '@remote-com/mushu/src/components/Success';
import SuccessMark from '@remote-com/mushu/src/components/Success/SuccessMark';
import { Checkbox } from '@remote-com/mushu/src/components/Ui/Form/Checkbox';
import { InputController } from '@remote-com/mushu/src/components/Ui/Form/Input';
import { GlobalStyles, theme as mushuTheme } from '@remote-com/mushu/src/config/styles';
import { CheckboxState } from '@remote-com/mushu/src/domains/registration/onboarding/shared/styles/Form';
import { useFormEvents } from '@remote-com/mushu/src/hooks/useFormEvents';
import { ButtonInline } from '@remote-com/norma/core/button';
import { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { dataLayerPush } from 'remote-com-shared/components/CookieScripts/helpers';
import {
  createPartnerStackSignup,
  getCurrentURL,
  registerLead,
} from 'remote-com-shared/components/ModalForm/helpers/services';
import {
  SubsribeSuccessContainer,
  FormError,
  DynamicFormWrapper,
} from 'remote-com-shared/components/ModalForm/styles';
import { getFields } from 'remote-com-shared/domains/hubspot/fields';
import { hasImpactFields } from 'remote-com-shared/domains/impact/helpers';
import { LocaleContextConsumer } from 'remote-com-shared/domains/locales/context';
import { useImpactFormValues } from 'remote-com-shared/domains/shared/trackingParameters';
import { getPartnerStackInfo } from 'remote-com-shared/utils/partnerstack';
import styled, { ThemeProvider } from 'styled-components';
import { v4 as uuid4 } from 'uuid';

import { ContentSectionButton } from './ContentSection';

const LocalSuccessMark = styled(SuccessMark)`
  svg {
    --circle-check-clr: #00b568;
    --circle-border-clr: #00b568;
  }
`;

const SuccessContainer = styled(SubsribeSuccessContainer)`
  margin-top: 40px;
  --sectionHighlightColor: #00b568;

  a {
    font-size: inherit;
  }
`;

const ApplyFormWrapper = styled.div`
  --dynamicFormContainerMargin: 0;
  --brandPrimary: #00234b;
  --checkboxStateSize: 16px;

  ${InputController} + ${InputController} {
    margin-top: 10px;
  }

  ${Checkbox} {
    padding: 20px 0;
  }

  ${CheckboxState} {
    --colors-irisBlue: #00b568;
    border-color: #00b568;
  }
`;

export const FormActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 30px;

  > * + * {
    margin-left: 16px;
  }
`;

export const CaptchaWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const lengthRequirementMessage = 'Must be at most ${max} characters'; // eslint-disable-line
export const nameMaxLength = 30;
export const nameLengthMessage = `First and last name combined must have a maximum of ${nameMaxLength} characters`;

export function ApplyForm({ hubspotFormDefinition, onSuccessChange }) {
  const { submitText, inlineMessage: successMessage, formFieldGroups } = hubspotFormDefinition;

  const captchaEnabled = true;

  const [showLoader, setShowLoader] = useState(false);
  const [showFormError, setShowFormError] = useState(false);
  const [isCaptchaDone, setIsCaptchaDone] = useState(!captchaEnabled);
  const [showSuccessState, setShowSuccessState] = useState(false);
  const reCaptchaRef = useRef(null);
  const impactFormValues = useImpactFormValues();

  const formName = `form-${hubspotFormDefinition.guid}`;

  const fields = getFields(formFieldGroups).map((field) => {
    switch (field.name) {
      case 'firstname': {
        field.schema = field.schema.max(nameMaxLength, nameLengthMessage);
        break;
      }
      case 'lastname': {
        field.schema = field.schema.when('firstname', (firstnameValue, schema) =>
          schema.max(nameMaxLength - (firstnameValue || '').length, nameLengthMessage)
        );
        break;
      }

      case 'email': {
        field.schema = field.schema.max(50, lengthRequirementMessage);
        break;
      }

      case 'company':
      case 'jobtitle':
      case 'city':
      case 'country': {
        field.schema = field.schema.max(30, lengthRequirementMessage);
        break;
      }
      default:
        break;
    }

    // Exception to remove job function
    const { name, label } = field;
    if (name.includes('job_function') && label.includes(' - ')) {
      const { placeholder } = field;
      const isPlaceholderLikeLabel = label === placeholder;
      const [, normalizedLabel] = label.split(' - ').map((entry) => entry.trim(entry));

      field.label = normalizedLabel;

      if (isPlaceholderLikeLabel) {
        field.placeholder = normalizedLabel;
      }
    }

    return field;
  });

  const { formState } = useFormEvents();
  const [formValues, setFormValues] = useState({});

  function handleCaptchaChange(value) {
    setIsCaptchaDone(value);
  }

  async function handleSubmit(values, { setSubmitting }) {
    setShowLoader(true);

    const updatedFormValues = {
      ...formValues,
      ...values,
    };
    setFormValues(updatedFormValues);

    if (isCaptchaDone) {
      const includePartnerstack =
        Object.prototype.hasOwnProperty.call(values, 'pspartnerkey') &&
        Object.prototype.hasOwnProperty.call(values, 'pspartner_group');
      const includeImpactValues = hasImpactFields(values);

      try {
        if (includePartnerstack) {
          try {
            const publicSlug = uuid4();
            await createPartnerStackSignup({
              name: values.name,
              email: publicSlug,
            });
          } catch {
            // Ignore as failing should not break hubspot
          }
        }

        const payload = {
          ...values,
          ...(Object.prototype.hasOwnProperty.call(values, 'utm')
            ? {
                utm: getCurrentURL(),
              }
            : {}),
          ...(includePartnerstack ? getPartnerStackInfo() : {}),
          ...(includeImpactValues ? impactFormValues : {}),
        };

        await registerLead(
          payload,
          `https://api.hsforms.com/submissions/v3/integration/submit/${hubspotFormDefinition.portalId}/${hubspotFormDefinition.guid}/`
        );

        dataLayerPush({
          event: 'user-submitted-hubspot-form',
          label: window.location.pathname,
          conversion: { send_to: 'AW-594140685/SLovCLa8nOEBEI28p5sC' },
        });

        setShowLoader(false);
        setShowSuccessState(true);
        if (typeof onSuccessChange === 'function') {
          onSuccessChange(true);
        }
      } catch (exception) {
        if (captchaEnabled) {
          reCaptchaRef.current.reset();
          setIsCaptchaDone(false);
        }
        setShowFormError(true);
        setShowLoader(false);
        setSubmitting(false);

        throw exception;
      }
    }
  }

  return (
    <ApplyFormWrapper>
      <ThemeProvider theme={{ mode: 'light', ...mushuTheme }}>
        <GlobalStyles />

        {showSuccessState ? (
          <SuccessContainer>
            <LocalSuccessMark />
            <SuccessTitle>Thank you!</SuccessTitle>
            <SuccessDescription dangerouslySetInnerHTML={{ __html: successMessage }} />
          </SuccessContainer>
        ) : (
          <DynamicFormWrapper>
            {showFormError && (
              <FormError>
                Something went wrong. Please try again, or contact{' '}
                <ButtonInline href="mailto:help@remote.com">help@remote.com</ButtonInline> if this
                error persists. Thank you.
              </FormError>
            )}

            <DynamicForm
              formName={formName}
              fields={fields}
              fieldValues={formValues}
              formError={formState.errorMessage}
              formTheme="dark"
              formSize="small"
              handleOnSubmit={handleSubmit}
              isLoading={formState.isLoading}
              enableReinitialize
            />

            {captchaEnabled && (
              <CaptchaWrapper>
                <LocaleContextConsumer>
                  {(locale) => (
                    <ReCAPTCHA
                      hl={locale}
                      ref={reCaptchaRef}
                      sitekey={process.env.NEXT_PUBLIC_CAPTCHA_KEY}
                      onChange={handleCaptchaChange}
                    />
                  )}
                </LocaleContextConsumer>
              </CaptchaWrapper>
            )}

            <FormActions>
              <ContentSectionButton
                as="button"
                type="submit"
                isLoading={showLoader}
                disabled={!isCaptchaDone}
                form={formName}
              >
                {submitText}
              </ContentSectionButton>
            </FormActions>
          </DynamicFormWrapper>
        )}
      </ThemeProvider>
    </ApplyFormWrapper>
  );
}
